import React from 'react';
import {
  AbsoluteFill,
  Composition,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

type ElementCategory =
  | 'alkali-metal'
  | 'alkaline-earth-metal'
  | 'transition-metal'
  | 'nonmetal'
  | 'noble-gas';

type ElementCell = {
  number: number;
  symbol: string;
  period: number;
  group: number;
  category: ElementCategory;
};

const BG = '#08090c';
const PRIMARY = '#f59e0b';
const ACCENT = '#06b6d4';

const CATEGORY_COLORS: Record<ElementCategory, string> = {
  'alkali-metal': '#ef4444',
  'alkaline-earth-metal': '#f97316',
  'transition-metal': '#eab308',
  nonmetal: '#22c55e',
  'noble-gas': '#a855f7',
};

const ELEMENTS: ElementCell[] = [
  { number: 1, symbol: 'H', period: 1, group: 1, category: 'nonmetal' },
  { number: 2, symbol: 'He', period: 1, group: 18, category: 'noble-gas' },
  { number: 3, symbol: 'Li', period: 2, group: 1, category: 'alkali-metal' },
  { number: 4, symbol: 'Be', period: 2, group: 2, category: 'alkaline-earth-metal' },
  { number: 5, symbol: 'B', period: 2, group: 13, category: 'nonmetal' },
  { number: 6, symbol: 'C', period: 2, group: 14, category: 'nonmetal' },
  { number: 7, symbol: 'N', period: 2, group: 15, category: 'nonmetal' },
  { number: 8, symbol: 'O', period: 2, group: 16, category: 'nonmetal' },
  { number: 9, symbol: 'F', period: 2, group: 17, category: 'nonmetal' },
  { number: 10, symbol: 'Ne', period: 2, group: 18, category: 'noble-gas' },
  { number: 11, symbol: 'Na', period: 3, group: 1, category: 'alkali-metal' },
  { number: 12, symbol: 'Mg', period: 3, group: 2, category: 'alkaline-earth-metal' },
  { number: 13, symbol: 'Al', period: 3, group: 13, category: 'nonmetal' },
  { number: 14, symbol: 'Si', period: 3, group: 14, category: 'nonmetal' },
  { number: 15, symbol: 'P', period: 3, group: 15, category: 'nonmetal' },
  { number: 16, symbol: 'S', period: 3, group: 16, category: 'nonmetal' },
  { number: 17, symbol: 'Cl', period: 3, group: 17, category: 'nonmetal' },
  { number: 18, symbol: 'Ar', period: 3, group: 18, category: 'noble-gas' },
  { number: 19, symbol: 'K', period: 4, group: 1, category: 'alkali-metal' },
  { number: 20, symbol: 'Ca', period: 4, group: 2, category: 'alkaline-earth-metal' },
  { number: 21, symbol: 'Sc', period: 4, group: 3, category: 'transition-metal' },
  { number: 22, symbol: 'Ti', period: 4, group: 4, category: 'transition-metal' },
  { number: 23, symbol: 'V', period: 4, group: 5, category: 'transition-metal' },
  { number: 24, symbol: 'Cr', period: 4, group: 6, category: 'transition-metal' },
  { number: 25, symbol: 'Mn', period: 4, group: 7, category: 'transition-metal' },
  { number: 26, symbol: 'Fe', period: 4, group: 8, category: 'transition-metal' },
  { number: 27, symbol: 'Co', period: 4, group: 9, category: 'transition-metal' },
  { number: 28, symbol: 'Ni', period: 4, group: 10, category: 'transition-metal' },
  { number: 29, symbol: 'Cu', period: 4, group: 11, category: 'transition-metal' },
  { number: 30, symbol: 'Zn', period: 4, group: 12, category: 'transition-metal' },
  { number: 31, symbol: 'Ga', period: 4, group: 13, category: 'nonmetal' },
  { number: 32, symbol: 'Ge', period: 4, group: 14, category: 'nonmetal' },
  { number: 33, symbol: 'As', period: 4, group: 15, category: 'nonmetal' },
  { number: 34, symbol: 'Se', period: 4, group: 16, category: 'nonmetal' },
  { number: 35, symbol: 'Br', period: 4, group: 17, category: 'nonmetal' },
  { number: 36, symbol: 'Kr', period: 4, group: 18, category: 'noble-gas' },
];

export const PeriodicTableAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        backgroundColor: BG,
        fontFamily: 'Inter, sans-serif',
        color: '#e5e7eb',
        padding: 48,
      }}
    >
      <h1
        style={{
          margin: 0,
          textAlign: 'center',
          fontSize: 80,
          fontWeight: 800,
          color: PRIMARY,
          letterSpacing: 2,
        }}
      >
        元素周期表
      </h1>

      <div
        style={{
          marginTop: 36,
          display: 'grid',
          gridTemplateColumns: 'repeat(18, 1fr)',
          gap: 10,
          height: 760,
        }}
      >
        {ELEMENTS.map((el) => {
          const rowStart = (el.period - 1) * 30;
          const fadeIn = interpolate(frame, [rowStart, rowStart + 20], [0, 1], {
            extrapolateRight: 'clamp',
          });
          const pop = spring({
            frame: frame - rowStart,
            fps,
            config: {
              damping: 16,
              stiffness: 140,
            },
          });
          const scale = interpolate(pop, [0, 1], [0.85, 1], {
            extrapolateRight: 'clamp',
          });

          return (
            <div
              key={el.number}
              style={{
                gridColumn: el.group,
                gridRow: el.period,
                borderRadius: 12,
                border: `2px solid ${CATEGORY_COLORS[el.category]}`,
                backgroundColor: `${CATEGORY_COLORS[el.category]}26`,
                opacity: fadeIn,
                transform: `scale(${scale})`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '8px 10px',
                boxShadow: `0 0 16px ${CATEGORY_COLORS[el.category]}55`,
              }}
            >
              <span
                style={{
                  fontSize: 24,
                  color: '#cbd5e1',
                }}
              >
                {el.number}
              </span>
              <span
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  color: '#f8fafc',
                  textAlign: 'center',
                }}
              >
                {el.symbol}
              </span>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 16,
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 48,
          color: '#cbd5e1',
          fontSize: 24,
        }}
      >
        {[
          ['碱金属', 'alkali-metal'],
          ['碱土金属', 'alkaline-earth-metal'],
          ['过渡金属', 'transition-metal'],
          ['非金属', 'nonmetal'],
          ['稀有气体', 'noble-gas'],
        ].map(([label, key]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 999,
                backgroundColor: CATEGORY_COLORS[key as ElementCategory],
                boxShadow: `0 0 10px ${CATEGORY_COLORS[key as ElementCategory]}`,
              }}
            />
            <span>{label}</span>
          </div>
        ))}
        <span style={{ color: ACCENT, marginLeft: 8 }}>18 列布局</span>
      </div>
    </AbsoluteFill>
  );
};

export const PeriodicTableAnimationComposition: React.FC = () => {
  return (
    <Composition
      id="PeriodicTableAnimation"
      component={PeriodicTableAnimation}
      durationInFrames={180}
      fps={30}
      width={1920}
      height={1080}
    />
  );
};
